import * as t from 'io-ts';
import {compose} from 'redux';

import {ApiConversationBatch} from '../../../src/models/api/conversationBatchModel';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {serializeConversationPatch} from '../../../src/models/api/conversationPatchModel';
import {
  ConversationTrackerModel,
  ConversationTrackerStagesEnum,
} from '../../../src/models/api/conversationTrackerModel';
import {mergeDataGroups} from '../../../src/models/api/dataGroupModel';
import {patchApiAsync} from '../../../src/services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../src/services/http/httpError';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {updateDataAction} from '../../../src/store/data/dataGroupsActions';
import {DataState} from '../../../src/store/data/dataState';
import {OfflineError} from '../../../srcCommon/services/offline/offlineError';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../helpers/updateHelpers';
import {createTaskType} from '../task';

/*
 * Task.
 */

export const RSubscribeToConversationTask = createTaskType({
  name: 'subscribeToConversationTask',
  payloadType: t.type({
    conversations: t.readonlyArray(
      t.type({
        id: t.number,
      }),
    ),
    teammateId: t.number,
    initiatedAt: t.union([t.number, t.undefined]),
  }),
  createOptimisticUpdater: ({conversations, teammateId}) =>
    createObjectPropertyUpdater<DataState, 'conversations'>(
      'conversations',
      createObjectPropertyUpdater(
        'byId',
        compose(
          ...conversations.map(({id}) =>
            createResourceMapValueUpdater(
              id,
              createUpdater<ConversationModel>((conversation) => {
                // If the user is not already a tracker, ignore.
                const tracker = conversation.trackers[teammateId];
                if (!tracker) {
                  return conversation;
                }

                const newTracker: ConversationTrackerModel = {
                  ...tracker,
                  stage: ConversationTrackerStagesEnum.SUBSCRIBER,
                };

                return {
                  ...conversation,
                  trackers: {
                    ...conversation.trackers,
                    [teammateId]: newTracker,
                  },
                };
              }),
            ),
          ),
        ),
      ),
    ),
  createPerformer:
    ({conversations, teammateId, initiatedAt}) =>
    async (_dispatch, getState) => {
      try {
        const company = getCompany(getState());
        const url = `${company.url}/conversations`;
        const body = {
          conversations: conversations.map(({id}) =>
            serializeConversationPatch(id, {
              updateTrackers: [
                {
                  teammateId,
                  stage: ConversationTrackerStagesEnum.SUBSCRIBER,
                },
              ],
              offlineMetadata: {
                initiatedAt,
              },
            }),
          ),
        };

        const {dataGroups} = await patchApiAsync(ApiConversationBatch, url, body);
        if (dataGroups.length !== conversations.length) {
          throw new Error(
            `Expected ${conversations.length} conversation batch results, received ${dataGroups.length}`,
          );
        }

        const dataGroup = mergeDataGroups(...dataGroups);
        return updateDataAction(dataGroup);
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        throw error;
      }
    },
});
